if (!customElements.get('filazoo-product-content')) {
  customElements.define(
    'filazoo-product-content',
    class FilazooProductTabs extends HTMLElement {
      connectedCallback() {
        this.nav = this.querySelector('[data-sticky-nav]');
        this.links = Array.from(this.querySelectorAll('[data-anchor-link]'));
        this.sections = Array.from(this.querySelectorAll('[data-content-section]'));
        this.header = document.querySelector('.section-header');

        this.onLinkClick = this.onLinkClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.refreshObserver = this.refreshObserver.bind(this);

        // Cheap listeners bind straight away so a tab stays clickable even
        // before the scroll-spy is wired up.
        this.links.forEach((link) => link.addEventListener('click', this.onLinkClick));

        if (window.Shopify?.designMode) {
          this.addEventListener('shopify:block:select', (event) => {
            const contentSection = event.target.closest('[data-content-section]');
            if (contentSection) this.scrollToSection(contentSection, false);
          });
        }

        // The section sits below the fold, so defer the observer setup and the
        // layout reads it needs until the reader is actually heading this way.
        if (this.needsImmediateSetup()) {
          this.setupScrollSpy();
        } else {
          this.lazyObserver = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.lazyObserver.disconnect();
              this.lazyObserver = null;
              this.setupScrollSpy();
            },
            { rootMargin: '200% 0px' }
          );
          this.lazyObserver.observe(this);
        }
      }

      needsImmediateSetup() {
        if (window.Shopify?.designMode) return true;
        const hash = window.location.hash.slice(1);
        return hash !== '' && this.sections.some((section) => section.id === hash);
      }

      setupScrollSpy() {
        if (this.scrollSpyReady) return;
        this.scrollSpyReady = true;

        window.addEventListener('resize', this.onResize, { passive: true });
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.refreshObserver();
      }

      disconnectedCallback() {
        this.links?.forEach((link) => link.removeEventListener('click', this.onLinkClick));
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.onScroll);
        this.observer?.disconnect();
        this.lazyObserver?.disconnect();
        clearTimeout(this.resizeTimer);
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      }

      getScrollOffset() {
        const navHeight = this.nav?.offsetHeight || 0;
        const headerBounds = this.header?.getBoundingClientRect();
        const headerHeight = headerBounds && headerBounds.bottom > 0 ? headerBounds.bottom : 0;
        return Math.max(0, headerHeight) + navHeight + 12;
      }

      onLinkClick(event) {
        const targetId = event.currentTarget.hash.slice(1);
        const target = document.getElementById(targetId);
        if (!target) return;

        event.preventDefault();
        // A click can land before the lazy setup has run.
        this.setupScrollSpy();
        this.setActive(targetId);
        this.scrollToSection(target, true);
        history.replaceState(null, '', `#${targetId}`);
      }

      scrollToSection(target, smooth = true) {
        const targetTop = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
        });
      }

      onResize() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(this.refreshObserver, 150);
      }

      refreshObserver() {
        this.observer?.disconnect();
        if (!this.sections.length) return;

        const topMargin = this.getScrollOffset();
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(entry.target.id);
            });
          },
          {
            rootMargin: `-${topMargin}px 0px -58% 0px`,
            threshold: 0,
          }
        );

        this.sections.forEach((section) => this.observer.observe(section));
        this.onScroll();
      }

      // Coalesce to one layout read per frame — the handler measures every
      // content section, so running it per scroll event thrashes layout.
      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateActiveFromScroll();
        });
      }

      updateActiveFromScroll() {
        if (!this.sections.length) return;
        const offset = this.getScrollOffset() + 2;
        let current = this.sections[0];

        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
          this.setActive(this.sections[this.sections.length - 1].id);
          return;
        }

        this.sections.forEach((section) => {
          if (section.getBoundingClientRect().top <= offset) current = section;
        });

        this.setActive(current.id);
      }

      // Scrolls only the nav strip, horizontally. scrollIntoView() would also
      // scroll the nearest scrollable ancestor — the page — so every scroll-spy
      // update yanked the reader further down the product page.
      centreNavLink(link) {
        const strip = this.nav;
        if (!strip) return;
        const max = strip.scrollWidth - strip.clientWidth;
        if (max <= 0) return;

        const stripBox = strip.getBoundingClientRect();
        const linkBox = link.getBoundingClientRect();
        const delta = linkBox.left + linkBox.width / 2 - (stripBox.left + stripBox.width / 2);
        const left = Math.min(Math.max(0, strip.scrollLeft + delta), max);
        if (Math.abs(left - strip.scrollLeft) < 1) return;
        strip.scrollTo({ left, behavior: 'smooth' });
      }

      setActive(sectionId) {
        if (this.activeSectionId === sectionId) return;
        this.activeSectionId = sectionId;

        this.links.forEach((link) => {
          const isActive = link.hash === `#${sectionId}`;
          link.classList.toggle('is-active', isActive);
          if (isActive) {
            link.setAttribute('aria-current', 'true');
            this.centreNavLink(link);
          } else {
            link.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
